import React, { useId } from "react";
// The card's layout, the one web-app-next's ReportShareCard (@wone/ui) and
// mobile draw from, and the report's radar geometry: data and pure functions,
// so this app draws the same card without running that React 19 component.
import CARD from "../../../../../../../../packages/assets/report-share/card.json";
import {
  orderByIds,
  radarLayout,
} from "../../../../../../../../packages/ui/src/components/report/geometry";

/** The card's size in pixels: a 4:5 portrait, the shape feeds crop to least. */
export const SHARE_CARD_WIDTH = CARD.canvas.width;
export const SHARE_CARD_HEIGHT = CARD.canvas.height;

type ColourName = keyof typeof CARD.colours;

/**
 * A colour the card names, as its value. web-app-next reads the report token
 * instead; this app has none, and the value is what the token holds.
 */
const colour = (name: string): string => {
  const c = CARD.colours[name as ColourName];
  if (!c) throw new Error(`card.json names an unknown colour: ${name}`);
  return c.value;
};

const face = (name: keyof typeof CARD.fonts): string => {
  const f = CARD.fonts[name];
  return `"${f.family}", ${f.fallback}`;
};

export interface ShareCardDomain {
  /** Stable key, matched against `order`. Not drawn. */
  id: string;
  /** Drawn as given. */
  label: string;
  /** Shapes the polygon only. No value is printed on the card. */
  score: number;
}

export interface ShareCardProps {
  /** What a domain score is out of: sizes the polygon. */
  outOf: number;
  /** Scored domains only. The radar is left out with fewer than three. */
  domains: ShareCardDomain[];
  /** Domain ids clockwise from twelve o'clock. */
  order?: string[];
  /** "My Resilience Report", from the catalogue. */
  heading: string;
  /** The image's accessible name, e.g. "WONE Index score: 82 out of 100". */
  title: string;
  /** The WONE mark, drawn white whatever its own colour. */
  logoSrc: string;
  /** A still of the report sky. Omit it and the sky gradient stands in. */
  skySrc?: string;
}

/**
 * The image a learner shares from the report: the WONE mark, its heading
 * and the domain radar, on the report sky, at 1080x1350.
 *
 * What it leaves out is the point: no overall score, name, company, Ori copy,
 * link, or any per-domain value. It takes nothing that could carry them, so a caller
 * cannot add them by accident.
 */
const ShareCard: React.FC<ShareCardProps> = ({
  outOf,
  domains,
  order,
  heading,
  title,
  logoSrc,
  skySrc,
}) => {
  const id = useId();
  const titleId = `${id}-title`;
  const skyId = `${id}-sky`;
  const whiteId = `${id}-white`;

  const ordered = orderByIds(domains, order);
  const layout =
    ordered.length >= CARD.radar.minDomains
      ? radarLayout(ordered, outOf)
      : null;
  const { logo, heading: headingText, radar } = CARD;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-labelledby={titleId}
      viewBox={`0 0 ${SHARE_CARD_WIDTH} ${SHARE_CARD_HEIGHT}`}
      width={SHARE_CARD_WIDTH}
      height={SHARE_CARD_HEIGHT}
      fontFamily={face("sans")}
    >
      <title id={titleId}>{title}</title>
      <defs>
        <linearGradient id={skyId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={colour("skyNear")} />
          <stop offset="1" stopColor={colour("skyFar")} />
        </linearGradient>
        {/* Every pixel of the mark white, keeping its alpha. */}
        <filter id={whiteId} colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 1 0"
          />
        </filter>
      </defs>

      <rect
        width={SHARE_CARD_WIDTH}
        height={SHARE_CARD_HEIGHT}
        fill={`url(#${skyId})`}
      />
      {skySrc && (
        <image
          href={skySrc}
          width={SHARE_CARD_WIDTH}
          height={SHARE_CARD_HEIGHT}
          preserveAspectRatio="xMidYMid slice"
        />
      )}
      <rect
        width={SHARE_CARD_WIDTH}
        height={SHARE_CARD_HEIGHT}
        fill={colour("scrim")}
      />

      <image
        href={logoSrc}
        x={logo.box.x}
        y={logo.box.y}
        width={logo.box.width}
        height={logo.box.height}
        preserveAspectRatio="xMidYMid meet"
        filter={`url(#${whiteId})`}
      />

      <text
        x={headingText.anchor.x}
        y={headingText.anchor.baseline}
        textAnchor="middle"
        fill={colour("white")}
        fontSize={headingText.size}
        fontWeight={headingText.weight}
        letterSpacing={headingText.letterSpacing}
      >
        {heading}
      </text>

      {layout && (
        <g
          data-part="radar"
          transform={`translate(${radar.origin.x},${radar.origin.y}) scale(${radar.scale})`}
        >
          <g
            fill="none"
            stroke={colour(radar.web.colour)}
            strokeOpacity={radar.web.opacity}
            strokeWidth={radar.web.strokeWidth}
          >
            {layout.rings.map((points) => (
              <polygon key={points} points={points} />
            ))}
            {layout.spokes.map((s, i) => (
              <line
                key={i}
                x1={layout.width / 2}
                y1={layout.height / 2}
                x2={s.x}
                y2={s.y}
              />
            ))}
          </g>
          <polygon
            points={layout.points.map((p) => `${p.x},${p.y}`).join(" ")}
            fill={colour(radar.polygon.fill)}
            stroke={colour(radar.polygon.stroke)}
            fillOpacity={radar.polygon.fillOpacity}
            strokeOpacity={radar.polygon.strokeOpacity}
            strokeWidth={radar.polygon.strokeWidth}
            strokeLinejoin="round"
          />
          {layout.points.map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r={radar.vertexDot.radius}
              fill={colour(radar.vertexDot.colour)}
            />
          ))}
          {layout.spokes.map((s, i) => (
            <circle
              key={i}
              cx={s.x}
              cy={s.y}
              r={radar.spokeEndDot.radius}
              fill={colour(radar.spokeEndDot.colour)}
            />
          ))}
          {layout.labels.map((l, i) => (
            <text
              key={ordered[i]?.id ?? i}
              x={l.x}
              y={l.y}
              textAnchor="middle"
              fill={colour(radar.label.colour)}
              fontSize={radar.label.size}
              fontWeight={radar.label.weight}
            >
              {l.lines.map((line, j) => (
                <tspan
                  key={j}
                  x={l.x}
                  dy={j === 0 ? l.nameDy : radar.units.lineHeight}
                >
                  {line}
                </tspan>
              ))}
            </text>
          ))}
        </g>
      )}
    </svg>
  );
};

export default ShareCard;
